import { useContext, useEffect, useState } from "react";
import { getData } from "src/actions/getData";
import MiniInfoButton from "src/components/MiniInfoButton";
import PageHeader from "src/components/PageHeader";
import Table from "src/components/Table";
import { numberWithCommas } from "src/utils/thousandSeprator";
import { TbHelpSquareRounded } from "react-icons/tb";
import { FilterFn } from "src/components/Table/FilterFn";
import FilterColumn from "src/components/Table/FilterColumn";
import moment from 'jalali-moment';
import Accordion from "src/components/Accordion";
import Hintbox from "src/components/Hintbox";
import userContext from "src/context/userContext";

export default function SellerSales() {
  const [sellerSales, setSellerSales] = useState([]);
  const [sellerInbounds, setSellerInbounds] = useState([]);
  const { user } = useContext(userContext);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (!sellerSales.length && !sellerInbounds.length) {
      getData(setSellerSales, `/seller/sales/${user.email}`, {}, signal);
      getData(setSellerInbounds, `/seller/inbounds`, {}, signal);
    }

    return () => controller.abort();
  }, []);

  // merge sales with inbound based on sku + orderId
  const mergedSales = sellerSales.map(sale => {
    const normalize = str => (str ? String(str).toLowerCase().trim() : "");
    const saleKey = `${normalize(sale.product_sku)}_${normalize(sale.order_id)}`;

    let inboundId = null;
    let inboundStatus = null;

    for (const inbound of sellerInbounds) {
      for (const p of inbound.inboundproducts) {
        const inboundKey = `${normalize(p.sku)}_${normalize(p.orderId)}`;
        if (inboundKey === saleKey) {
          inboundId = inbound.id;

          const delivered = Number(p.deliveredQuantity) || 0;
          const totalQty = Number(sale.quantity) || 0;

          if (delivered === totalQty) inboundStatus = "delivered";
          else if (delivered > 0 && delivered < totalQty) inboundStatus = "partial";
          else inboundStatus = "pending";

          break;
        }
      }
      if (inboundId) break;
    }

    return { ...sale, inboundId, inboundStatus };
  });

  const columns = [
    { id: "product_image", header: "تصویر", cell: info => <img className="w-[80px] max-w-[80px] rounded-lg" src={info.getValue()} /> },
    { id: "product_name", header: "نام محصول", cell: info => info.getValue() || "---" },
    { id: "product_sku", header: "SKU", cell: info => info.getValue() || "---" },
    { id: "quantity", header: "تعداد", cell: info => info.getValue() || "---" },
    { id: "item_price", header: "مبلغ فروش رفته", cell: info => numberWithCommas(info.getValue()) + " تومان" },
    { id: "item_total", header: "مبلغ کل فروش رفته", cell: info => numberWithCommas(info.getValue()) + " تومان" },
    {
      id: "order_status_fa",
      header: "وضعیت سفارش",
      cell: info => {
        const val = info.getValue();
        if (!val) return "---";
        if (val === "تکمیل شده") return <MiniInfoButton text="تحویل مشتری شده" backgroundColor="#008000" />;
        if (val === "بسته بندی شده") return <MiniInfoButton text="بسته بندی شده" backgroundColor="#ecd009" />;
        if (val !== "در حال انجام") return <MiniInfoButton text="در راه" backgroundColor="#0000FF" />;
        return <MiniInfoButton text="جمع آوری نشده" color={info.row.original.color} />;
      }
    },
    { id: "order_id", header: "شماره سفارش", cell: info => info.getValue() || "---" },
    {
      id: "order_date",
      header: "تاریخ سفارش",
      cell: info => info.getValue() ? moment.from(info.getValue(), "en", "YYYY-MM-DD HH:mm:ss").locale("fa").format("jYYYY/jMM/jDD HH:mm") : "---",
      filterFn: (row, columnId, filterValue) => FilterFn(row, columnId, filterValue, "date"),
      meta: { filterComponent: setFilterValue => <FilterColumn setFilterValue={setFilterValue} type="date" /> }
    },
    { id: "inboundId", header: "شماره محموله", cell: info => info.getValue() || "---" },
    {
      id: "inboundStatus",
      header: "وضعیت محموله",
      cell: info => {
        const val = info.getValue();
        if (!val) return "---";
        if (val === "pending") return <MiniInfoButton text="در انتظار" backgroundColor="#ecd009" />;
        if (val === "partial") return <MiniInfoButton text="دریافت جزئی" backgroundColor="#FFA500" />;
        if (val === "delivered") return <MiniInfoButton text="دریافت شده" backgroundColor="#008000" />;
        return <MiniInfoButton text={val} backgroundColor="#0000FF" />;
      }
    },
  ];
  
  return (
    <>
      <PageHeader title="لیست فروش‌ها" />
      <Accordion title="نکات راهنما" icon={<TbHelpSquareRounded className="text-gray-400" />} hasVerticalLine={true}>
        <Hintbox text="گزارش های فروش ارائه شده در این صفحه قطعی نیستند و در سفارشاتی که کمتر از 7 روز از ارسال آن‌ها می‌گذرد ممکن است تغییر کنند(برگشت مشتری از خرید و ...)، همچنین این گزارشات هر 24 ساعت یکبار به روز می‌شوند" type="info" />
      </Accordion>
      <Table data={mergedSales} columns={columns} CSVExportPermission="viewDashboard" tableName="marketingSales" />
    </>
  );
}
